import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { subscribeToUnreadCount } from '../services/chatService';
import { useWindowSize, BP } from '../hooks/useWindowSize';
import packageJson from '../../package.json';

const version = packageJson.version;

const COLORS = {
  navy: '#0F1E2E',
  navyLight: '#1a3048',
  navyMid: '#1E3048',
  orange: '#E8590C',
  orangeHover: 'rgba(232,89,12,0.12)',
  text: '#e0eaf2',
  textMuted: '#8aa0b0',
  textDim: '#b0c4d4',
  border: '#1a3048',
  bg: '#f0f4f8',
  white: '#ffffff',
  red: '#ef4444',
};

function getPageTitle(pathname) {
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/manage-jobs') return 'Manage Jobs';
  if (pathname === '/messages') return 'Messages';
  if (pathname === '/profile') return 'My Profile';
  if (pathname === '/post-job') return 'Post a Job';
  if (pathname === '/payment-setup') return 'Payment Setup';
  if (pathname === '/i9-schedule') return 'I-9 Schedule';
  if (pathname === '/offer-letters') return 'Offer Letters';
  if (pathname.startsWith('/edit-job/')) return 'Edit Job';
  if (pathname.match(/^\/jobs\/[^/]+\/applicants/)) return 'Applicants';
  if (pathname === '/applicants') return 'All Applicants';
  if (pathname === '/hired') return 'All Hired';
  if (pathname.startsWith('/chat/')) return 'Chat';
  if (pathname === '/account/profile') return 'Company Profile';
  if (pathname === '/account/settings') return 'Settings';
  return 'WorkrMatch';
}

const NAV = [
  {
    section: 'MAIN',
    items: [
      {
        label: 'Dashboard',
        path: '/dashboard',
        matchFn: (p) => p === '/dashboard',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
        ),
      },
      {
        label: 'Messages',
        path: '/messages',
        matchFn: (p) => p === '/messages' || p.startsWith('/chat'),
        badge: true,
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        ),
      },
    ],
  },
  {
    section: 'HIRING',
    items: [
      {
        label: 'Manage Jobs',
        path: '/manage-jobs',
        matchFn: (p) => p === '/manage-jobs' || p.startsWith('/edit-job'),
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
          </svg>
        ),
      },
      {
        label: 'Offer Letters',
        path: '/offer-letters',
        matchFn: (p) => p === '/offer-letters',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
        ),
      },
      {
        label: 'I-9 Verification',
        path: '/i9-schedule',
        matchFn: (p) => p === '/i9-schedule',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
          </svg>
        ),
      },
    ],
  },
  {
    section: 'ACCOUNT',
    employerOnly: true,
    items: [
      {
        label: 'My Profile',
        path: '/profile',
        matchFn: (p) => p === '/profile',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
          </svg>
        ),
      },
      {
        label: 'Payment Setup',
        path: '/payment-setup',
        matchFn: (p) => p === '/payment-setup',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
        ),
      },
      {
        label: 'Company Profile',
        path: '/account/profile',
        matchFn: (p) => p === '/account/profile',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
          </svg>
        ),
      },
      {
        label: 'Settings',
        path: '/account/settings',
        matchFn: (p) => p === '/account/settings',
        icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        ),
      },
    ],
  },
];

function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const isTablet = width >= BP.mobile && width < BP.tablet;
  const isDesktop = width >= BP.tablet;
  const sidebarWidth = isDesktop ? 220 : isTablet ? 64 : 0;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [employerName, setEmployerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [companyLogoURL, setCompanyLogoURL] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setEmployerName(data.displayName || data.email || '');
        setCompanyName(data.profile?.company || '');
        setRole(data.profile?.role || data.role || '');
        setPhotoURL(data.photoURL || '');
        setCompanyLogoURL(data.companyLogoURL || '');
      }
    });
    const unsub = subscribeToUnreadCount(user.uid, setUnreadCount);
    return () => unsub();
  }, []);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const pageTitle = getPageTitle(location.pathname);
  const initials = (employerName || '?')[0].toUpperCase();

  const sectionLabelStyle = {
    fontSize: '10px',
    color: '#4a6878',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    padding: '20px 16px 6px',
    fontWeight: '600',
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>

      {/* Hamburger — fixed position, mobile only, hidden when sidebar open */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
          style={{
            position: 'fixed',
            top: '8px',
            left: '8px',
            zIndex: 1001,
            width: '44px',
            height: '44px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.navy,
            borderRadius: '8px',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      )}

      {/* Backdrop for mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999,
          }}
        />
      )}

      {/* Sidebar */}
      <div style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: isMobile ? '280px' : `${sidebarWidth}px`,
        height: '100vh',
        backgroundColor: COLORS.navy,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: 1000,
        transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.25s ease',
      }}>

        {/* Logo */}
        <div style={{
          padding: '14px 12px',
          borderBottom: `1px solid ${COLORS.navyLight}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isTablet && !sidebarOpen ? 'center' : 'flex-start',
          gap: '10px',
          minWidth: 0,
          position: 'relative',
        }}>
          {/* Logo or initial */}
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: COLORS.navyLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px',
            boxSizing: 'border-box',
          }}>
            {companyLogoURL ? (
              <img src={companyLogoURL} alt="logo" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: '16px', fontWeight: '700', color: COLORS.orange }}>
                {(companyName || employerName || '?')[0].toUpperCase()}
              </span>
            )}
          </div>

          {/* Company name — hidden in tablet icon-only mode */}
          {(!isTablet || sidebarOpen) && (
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: COLORS.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '1.2',
              }}>
                {companyName || employerName || 'My Company'}
              </div>
            </div>
          )}

          {/* X close button — mobile only, when sidebar open */}
          {isMobile && sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '36px',
                height: '36px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.textMuted,
                borderRadius: '6px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* Nav */}
        <div style={{ flex: 1, paddingBottom: '8px' }}>
          {NAV.map(({ section, items, employerOnly }) => {
            if (employerOnly && role !== 'employer') return null;
            return (
            <div key={section}>
              {(!isTablet || sidebarOpen) && <div style={sectionLabelStyle}>{section}</div>}
              {isTablet && !sidebarOpen && <div style={{ height: '12px' }} />}
              {items.map((item) => {
                const isActive = item.matchFn(location.pathname);
                const key = `${section}-${item.label}`;
                const hovered = hoveredItem === key;
                const iconOnly = isTablet && !sidebarOpen;
                return (
                  <div
                    key={key}
                    onClick={() => { navigate(item.path); if (isMobile) setSidebarOpen(false); }}
                    onMouseEnter={() => setHoveredItem(key)}
                    onMouseLeave={() => setHoveredItem(null)}
                    title={iconOnly ? item.label : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: iconOnly ? 'center' : 'flex-start',
                      gap: '10px',
                      padding: iconOnly ? '12px 0' : '9px 16px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      borderLeft: isActive ? `3px solid ${COLORS.orange}` : '3px solid transparent',
                      color: isActive ? COLORS.white : hovered ? COLORS.textDim : COLORS.textDim,
                      backgroundColor: isActive ? COLORS.orangeHover : hovered ? 'rgba(255,255,255,0.04)' : 'transparent',
                      position: 'relative',
                    }}
                  >
                    {item.icon}
                    {!iconOnly && <span style={{ flex: 1 }}>{item.label}</span>}
                    {item.badge && unreadCount > 0 && (
                      <div style={{
                        backgroundColor: COLORS.red,
                        color: COLORS.white,
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '1px 6px',
                        minWidth: '16px',
                        textAlign: 'center',
                        ...(iconOnly ? { position: 'absolute', top: '6px', right: '6px', padding: '1px 4px', fontSize: '9px' } : {}),
                      }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
          })}
        </div>

        {/* Version */}
        {(!isTablet || sidebarOpen) && (
          <div style={{ fontSize: '11px', color: '#3a5568', textAlign: 'center', padding: '8px 16px 4px', display: 'block', flexShrink: 0 }}>
            WorkrMatch Admin v{version}
          </div>
        )}

        {/* Sign out */}
        <div
          onClick={handleSignOut}
          onMouseEnter={() => setHoveredItem('signout')}
          onMouseLeave={() => setHoveredItem(null)}
          title={isTablet && !sidebarOpen ? 'Sign out' : undefined}
          style={{
            borderTop: `1px solid ${COLORS.navyLight}`,
            padding: isTablet && !sidebarOpen ? '12px 0' : '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isTablet && !sidebarOpen ? 'center' : 'flex-start',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '13px',
            color: hoveredItem === 'signout' ? COLORS.textDim : COLORS.textMuted,
            backgroundColor: hoveredItem === 'signout' ? 'rgba(255,255,255,0.04)' : 'transparent',
            flexShrink: 0,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          {(!isTablet || sidebarOpen) && 'Sign out'}
        </div>
      </div>

      {/* Main area */}
      <div style={{ marginLeft: isMobile ? '0px' : `${sidebarWidth}px`, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: COLORS.bg, minWidth: 0, transition: 'margin-left 0.25s ease', overflowX: 'hidden', maxWidth: '100vw', boxSizing: 'border-box', width: isMobile ? '100%' : undefined }}>

        {/* Topbar */}
        <div style={{
          backgroundColor: '#F8FAFC',
          borderBottom: '1px solid #e2e8f0',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '0 12px 0 60px' : '0 24px',
          flexShrink: 0,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: '16px', fontWeight: '600', color: COLORS.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pageTitle}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            {photoURL ? (
              <img src={photoURL} alt="profile" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: COLORS.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontSize: '13px', fontWeight: '600', flexShrink: 0 }}>
                {initials}
              </div>
            )}
            {!isMobile && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.navy, lineHeight: '1.2' }}>{employerName || ''}</div>
                <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.2' }}>
                  {role ? role.charAt(0).toUpperCase() + role.slice(1) : ''}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Page content */}
        <div style={{
          padding: isMobile ? '16px 12px' : '24px',
          flex: 1,
          overflowX: 'hidden',
          overflowY: 'auto',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
            {children}
          </div>
        </div>
      </div>

    </div>
  );
}

export default AppShell;
